import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  Ref,
} from 'react';

import { InputClearButtonSuffix } from '#/components/Input/affixes/InputClearButtonSuffix';
import { InputIconAffix } from '#/components/Input/affixes/InputIconAffix';
import { Input } from '#/components/Input/Input';
import type {
  InputAffixVariant,
  InputStyleVariant,
} from '#/components/Input/Input.types';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';

import { InputIconContainer } from './InputIconContainer';
import { LocationInputWrapper } from './LocationInputWrapper';

type Props = {
  inputRef?: Ref<HTMLInputElement>;
  autoComplete?: string;
  showLocationIcon: boolean;
  onClear: (() => void) | null | undefined;
  className?: string;
  disabled?: boolean;
  gMapsErrored: boolean;
  qaId: string;
  hasSuccess?: boolean;
  value: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  title?: string;
  role?: 'combobox';
  'aria-label'?: string;
  'aria-autocomplete'?: 'none' | 'inline' | 'list' | 'both' | undefined;
  'aria-controls'?: string;
  'aria-activedescendant'?: string;
  'aria-expanded'?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
};

export function StyledInput({
  inputRef,
  autoComplete,
  showLocationIcon,
  onClear,
  className,
  disabled,
  gMapsErrored,
  id,
  onChange,
  onBlur,
  onKeyDown,
  onKeyPress,
  onFocus,
  placeholder,
  required,
  affixVariant,
  styleVariant,
  title,
  value,
  ...inputProps
}: Props) {
  const { i18n } = useI18n();

  return (
    <Tooltip
      closeable
      variant="brand-blue"
      placement="bottom-center"
      size="large"
      renderCondition={gMapsErrored}
      content={i18n(
        'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.',
      )}
    >
      <LocationInputWrapper className={className}>
        <Input
          fullWidth
          inputProps={{
            ref: inputRef,
            qaId: inputProps.qaId,
            disabled,
            value,
            autoComplete,
            role: inputProps.role,
            'aria-label': inputProps['aria-label'],
            'aria-autocomplete': inputProps['aria-autocomplete'],
            'aria-controls': inputProps['aria-controls'],
            'aria-activedescendant': inputProps['aria-activedescendant'],
            'aria-expanded': inputProps['aria-expanded'],
            id,
            placeholder,
            required,
            title,
            onChange,
            onBlur,
            onFocus,
            onKeyDown,
            onKeyPress,
          }}
          prefix={
            showLocationIcon ? (
              <InputIconContainer>
                <InputIconAffix
                  overrideIconColor="darkContentGrey"
                  iconName="location"
                  size={24}
                  noPaddingRight
                />
              </InputIconContainer>
            ) : null
          }
          affixVariant={affixVariant}
          styleVariant={styleVariant}
          suffix={
            onClear ? (
              <InputClearButtonSuffix
                ariaLabel={i18n('Clear location')}
                onClear={onClear}
                qaId={`${inputProps.qaId}-clear`}
              />
            ) : null
          }
          hideSuffixWhenEmpty
        />
      </LocationInputWrapper>
    </Tooltip>
  );
}
